import {
  Box,
  Icon,
  VStack,
  Text,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  IconButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import NotificationIcon from '@/assets/svg/sidebar/notification-bell.svg?react';
import { CloseIcon } from '@chakra-ui/icons';
import { NotificationsResponse } from '@/services/notifications';
import NotificationList from './NotificationList';
import useNotifications from '@/hooks/useNotification';
import { useQueryClient } from 'react-query';

const Notification = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const notificationRef = useRef<HTMLDivElement | null>(null);
  const { notifications, markAsAllRead } = useNotifications();

  const queryClient = useQueryClient();
  const hasRefetchedOnOpen = useRef(false);

  const labels: (keyof NotificationsResponse)[] = ['Order', 'Inventory', 'Payment', 'Promotion'];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('[aria-label="Toggle notifications"]')
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const hasUnreadNotifications = Object.keys(notifications || {}).some((category) =>
    notifications?.[category as keyof NotificationsResponse]?.some(
      (notification) => !notification.is_read,
    ),
  );

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const willOpen = !isOpen;

    setIsOpen(willOpen);

    if (willOpen && !hasRefetchedOnOpen.current) {
      hasRefetchedOnOpen.current = true;
      queryClient.refetchQueries({ queryKey: ['notifications'] });
    }
  };

  const popupWidth = useBreakpointValue({ base: '90vw', md: '483px', lg: '483px' });
  const popupRight = useBreakpointValue({ base: '-68px', md: '0' });
  const fontSizeTitle = useBreakpointValue({ base: 'lg', md: 'xl' });
  const fontSizeTab = useBreakpointValue({ base: 'xs', md: 'sm' });
  const fontSizeButton = useBreakpointValue({ base: 'xs', md: 'sm' });
  const iconSize = useBreakpointValue({ base: 5, md: 6 });
  const paddingX = useBreakpointValue({ base: 3, md: 6 });
  const paddingY = useBreakpointValue({ base: 2, md: '14px' });

  return (
    <Box position="relative" fontFamily={'primary'}>
      <Box position="relative">
        <IconButton
          icon={<NotificationIcon />}
          boxSize={iconSize}
          cursor="pointer"
          onClick={handleToggle}
          aria-label="Toggle notifications"
          variant="ghost"
        />
        {hasUnreadNotifications && (
          <Box
            position="absolute"
            top={0.5}
            right={1}
            width="8px"
            height="8px"
            borderRadius="50%"
            bg="red.500"
            border="1px solid white"
          />
        )}
      </Box>
      <Box
        position="absolute"
        top="30px"
        right={popupRight}
        background="white"
        width={popupWidth}
        display={isOpen ? 'block' : 'none'}
        zIndex={1000000}
        border="1px solid"
        borderColor="border.default"
        ref={notificationRef}
        borderRadius="xl"
        overflow="hidden"
      >
        <VStack align="start" spacing={0} width={'100%'}>
          <HStack
            justifyContent={'space-between'}
            width={'100%'}
            px={paddingX}
            py={paddingY}
            borderBottom={'2px solid'}
            borderColor={'border.default'}
          >
            <Text fontSize={fontSizeTitle} fontWeight="semibold">
              Notifications
            </Text>
            <HStack spacing={4}>
              <Text
                as={'button'}
                fontSize={fontSizeButton}
                color={hasUnreadNotifications ? 'brand.secondary.DEFAULT' : 'text.disable'}
                cursor={hasUnreadNotifications ? 'pointer' : 'not-allowed'}
                onClick={() => markAsAllRead()}
                disabled={!hasUnreadNotifications}
                _focusVisible={{
                  background: 'brand.primary.50',
                  color: 'brand.primary.DEFAULT',
                  outline: '2px solid',
                  outlineColor: 'brand.primary.DEFAULT',
                  outlineOffset: '2px',
                  borderRadius: '5px',
                }}
              >
                Mark all as read
              </Text>
              <Icon as={CloseIcon} boxSize={3} cursor="pointer" onClick={() => setIsOpen(false)} />
            </HStack>
          </HStack>
          <HStack width={'100%'}>
            <Tabs
              index={selectedTabIndex}
              onChange={(index) => setSelectedTabIndex(index)}
              width={'100%'}
            >
              <TabList
                justifyContent="space-around"
                width="100%"
                overflowX={{ base: 'scroll', md: 'hidden' }}
                overflowY={{ base: 'hidden', md: 'hidden' }} // Explicitly control vertical overflow
                whiteSpace="nowrap"
              >
                {labels.map((label, index) => {
                  const unreadCount =
                    notifications?.[label]?.filter((notification) => !notification.is_read)
                      ?.length || 0;

                  return (
                    <Tab
                      key={index}
                      width="100%"
                      fontSize="sm"
                      fontWeight="semibold"
                      color="text.primary"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={2}
                      _selected={{
                        borderBottom: '2px solid',
                        borderColor: 'brand.primary.DEFAULT',
                        color: 'brand.primary.DEFAULT',
                      }}
                      _active={{
                        background: 'none',
                      }}
                      _focusVisible={{
                        background: 'brand.primary.50',
                        color: 'brand.primary.DEFAULT',
                        outline: '2px solid',
                        outlineColor: 'brand.primary.DEFAULT',
                        outlineOffset: '2px',
                        boxShadow: 'none',
                      }}
                      _focus={{
                        boxShadow: 'none',
                      }}
                    >
                      {label}
                      <Box
                        width="16px"
                        height="16px"
                        display="flex"
                        border="none"
                        borderRadius="10px"
                        alignItems="center"
                        justifyContent="center"
                        fontSize={fontSizeTab}
                        fontWeight="bold"
                        color={selectedTabIndex === index ? 'brand.primary.DEFAULT' : '#344054'}
                        bg="background.secondary"
                        pb={0}
                        borderColor={
                          selectedTabIndex === index ? 'brand.primary.DEFAULT' : 'border.default'
                        }
                      >
                        <Text
                          fontSize={fontSizeTab === 'xs' ? '8px' : '10px'}
                          color={
                            selectedTabIndex === index ? 'brand.primary.DEFAULT' : 'text.secondary'
                          }
                        >
                          {unreadCount}
                        </Text>
                      </Box>
                    </Tab>
                  );
                })}
              </TabList>

              <TabPanels bg="white">
                {labels.map((label, index) => (
                  <TabPanel key={index} px={0} py={0}>
                    <NotificationList
                      items={notifications?.[label]}
                      category={label}
                      isActive={selectedTabIndex === index}
                    />
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default Notification;
